import React, { useEffect, useMemo, useState } from 'react';
import { LuMapPinned, LuMessageCircle, LuCheck, LuChevronDown, LuChevronUp } from 'react-icons/lu';
import api from '../../services/api';
import { getTodayISO, fechaAR } from '../../utils/dateUtils';
import { SEMAFORO, semaforo, kmEntre } from '../mapa/vistasMapa';
import { leerOferta, mensajeOferta, ofrecerPorWhatsApp, haceCuanto, diaVisita, OFERTA_DEFECTO } from '../../utils/ofertaMantenimiento';

const RADIOS = [1, 2, 3, 5];
const REOFRECER_DIAS = 30;
const km = v => (v < 1 ? `${Math.round(v * 1000)} m` : `${v.toLocaleString('es-AR', { maximumFractionDigits: 1 })} km`);
const reciente = (en) => !!en && Date.now() - new Date(en).getTime() < REOFRECER_DIAS * 86400000;
const FILTROS = [
    { id: 'ofrecer', label: 'Para ofrecer', pasa: x => (x.semaforo === 'rojo' || x.semaforo === 'amarillo') && !reciente(x.ofrecidoEn) },
    ...SEMAFORO.map(s => ({ id: s.id, label: s.corto, color: s.color, pasa: x => x.semaforo === s.id })),
    { id: 'todos', label: 'Todos', pasa: () => true },
];

// "Cerca de este trabajo" (20-oct-2026): en el resumen del trabajo nuevo, los clientes que están a
// pocas cuadras, del más cercano al más lejano, con el color del Radar. Si el trabajo es a futuro,
// el mensaje dice "el jueves 23/10 vamos a estar por tu zona". Lo manda el admin desde WhatsApp.
export default function CercaDelTrabajo({ hook }) {
    const { clienteId, itemActual, ticketItems, puntoLibre, fechaServicio } = hook;
    const [data, setData] = useState(null);
    const [abierto, setAbierto] = useState(false);
    const [radio, setRadio] = useState(2);
    const [filtro, setFiltro] = useState('ofrecer');
    const [oferta, setOferta] = useState(OFERTA_DEFECTO);
    const [ofrecidos, setOfrecidos] = useState({});   // clienteId → fecha (los de esta pantalla)

    useEffect(() => {
        let vivo = true;
        api.get('/mapa').then(r => { if (vivo) setData(r.data); }).catch(() => { /* sin mapa: no se muestra */ });
        leerOferta().then(o => vivo && setOferta(o));
        return () => { vivo = false; };
    }, []);

    // Dónde es el trabajo: el lugar elegido, si no el cliente, si no el punto del cliente nuevo
    const centro = useMemo(() => {
        if (!data) return null;
        const sedeId = itemActual?.sedeId || ticketItems?.find(i => i.sedeId)?.sedeId;
        const lugares = data.clientes || [];
        const p = (sedeId && lugares.find(c => c.sedeId === Number(sedeId) && c.lat != null))
            || (clienteId && lugares.find(c => String(c.clienteId) === String(clienteId) && c.lat != null));
        if (p) return { lat: p.lat, lng: p.lng, clienteId: p.clienteId };
        if (puntoLibre?.lat != null) return { lat: puntoLibre.lat, lng: puntoLibre.lng, clienteId: null };
        return null;
    }, [data, clienteId, itemActual?.sedeId, ticketItems, puntoLibre]);

    const cerca = useMemo(() => {
        if (!centro) return [];
        return (data.clientes || [])
            .filter(c => c.lat != null && String(c.clienteId) !== String(centro.clienteId ?? ''))
            .map(c => ({ ...c, semaforo: semaforo(c), km: kmEntre(centro, c), ofrecidoEn: ofrecidos[c.clienteId] || c.ofrecidoEn }))
            .filter(c => c.km <= radio)
            .sort((a, b) => a.km - b.km);
    }, [data, centro, radio, ofrecidos]);

    if (!centro) return null;
    const cuenta = (id) => cerca.filter(FILTROS.find(f => f.id === id).pasa).length;
    const lista = cerca.filter(FILTROS.find(f => f.id === filtro).pasa).slice(0, 40);
    const hoy = getTodayISO();
    const zona = fechaServicio && fechaServicio > hoy ? diaVisita(fechaServicio) : fechaServicio === hoy ? 'hoy' : '';

    const ofrecer = async (x) => {
        const tipo = x.radar?.tipo === 'FILTRO' ? 'FILTRO' : 'SANITIZACION';
        const en = await ofrecerPorWhatsApp({ telefono: x.telefono, clienteId: x.clienteId, sedeId: x.sedeId || null, tipo, origen: 'CERCA',
            mensaje: mensajeOferta({ cliente: x.cliente, tipo, fechaUltimo: x.ultimaVisita, oferta, zona }) });
        if (en) setOfrecidos(o => ({ ...o, [x.clienteId]: en }));
    };

    return (
        <div className="rounded-2xl bg-panel border border-black/[0.07] dark:border-white/[0.07] overflow-hidden">
            <button type="button" onClick={() => setAbierto(a => !a)} className="w-full flex items-center gap-3 p-4 text-left active:scale-[0.99]">
                <LuMapPinned size={18} className="text-brand-red shrink-0" />
                <span className="flex-1 min-w-0">
                    <span className="block text-body font-black text-ink">Cerca de este trabajo</span>
                    <span className="block text-caption text-muted">
                        {cuenta('ofrecer') ? `${cuenta('ofrecer')} para ofrecer mantenimiento a ${radio} km` : `Nadie para ofrecer a ${radio} km`}
                        {zona ? ` · vas ${zona}` : ''}
                    </span>
                </span>
                {abierto ? <LuChevronUp size={18} className="text-muted" /> : <LuChevronDown size={18} className="text-muted" />}
            </button>
            {abierto && (
                <div className="px-4 pb-4 space-y-2.5">
                    <div className="flex items-center gap-1.5">
                        <span className="text-caption text-secondary mr-1">Radio</span>
                        {RADIOS.map(r => (
                            <button key={r} type="button" onClick={() => setRadio(r)}
                                className={`h-8 px-3 rounded-lg text-label font-black ${radio === r ? 'bg-ink text-page' : 'bg-chip text-secondary'}`}>{r} km</button>
                        ))}
                    </div>
                    <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
                        {FILTROS.map(f => (
                            <button key={f.id} type="button" onClick={() => setFiltro(f.id)}
                                className={`shrink-0 h-8 px-2.5 rounded-full text-label font-black inline-flex items-center gap-1.5 ${filtro === f.id ? 'bg-ink text-page' : 'bg-chip text-secondary'}`}>
                                {f.color && <span className="w-2 h-2 rounded-full" style={{ background: f.color }} />}{f.label} <span className="opacity-60">{cuenta(f.id)}</span>
                            </button>
                        ))}
                    </div>
                    {lista.length === 0 ? (
                        <p className="text-caption text-muted py-2">No hay ninguno en este radio. Probá con más km.</p>
                    ) : (
                        <div className="rounded-xl bg-card divide-y divide-black/[0.05] dark:divide-white/[0.05]">
                            {lista.map(x => {
                                const s = SEMAFORO.find(z => z.id === x.semaforo);
                                const ya = reciente(x.ofrecidoEn);
                                return (
                                    <div key={x.id} className="flex items-center gap-2.5 px-3 py-2.5">
                                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: s?.color }} title={s?.label} />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-body font-bold text-ink truncate">{x.cliente}{x.sede && x.sede !== x.cliente ? ` · ${x.sede}` : ''}</p>
                                            <p className="text-label text-muted truncate">
                                                {km(x.km)} · {x.ultimaVisita ? `últ. ${fechaAR(x.ultimaVisita)}` : 'sin service'}
                                                {x.radar?.tipo === 'FILTRO' ? ' · filtro' : ''}
                                                {ya ? ` · ofrecido ${haceCuanto(x.ofrecidoEn)}` : ''}
                                                {x.conPortal ? ' · tiene portal' : ''}
                                            </p>
                                        </div>
                                        {x.semaforo === 'verde' ? <span className="shrink-0 text-label font-bold text-muted px-2">Al día</span> : (
                                        <button type="button" onClick={() => ofrecer(x)} aria-label={`Ofrecer mantenimiento a ${x.cliente}`}
                                            className={`shrink-0 h-9 px-3 rounded-lg text-label font-black inline-flex items-center gap-1.5 active:scale-95 ${ya ? 'bg-chip text-muted' : 'bg-[#25D366] text-white'}`}>
                                            {ya ? <LuCheck size={14} /> : <LuMessageCircle size={14} />} {ya ? 'Ofrecido' : 'Ofrecer'}
                                        </button>)}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    <p className="text-label text-muted">El mensaje se abre en WhatsApp para que lo revises y lo mandes vos.{zona ? ` Dice que ${zona} van a estar por la zona.` : ''}</p>
                </div>
            )}
        </div>
    );
}
